import React, { useCallback, useContext } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { StandardGrid, GridElement } from '@wix/da-shared-react/pkg/Grid';
import { MeasuredCookieType } from '@wix/da-shared-react/pkg/Measured';
import { getStreamItemId } from '@wix/da-shared-react/pkg/Duperbrowse/redux/helpers';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import ShopCard from '@wix/da-shared-react/pkg/ShopCard';
import { getItemId, getItemType, isDeviation } from '../../helpers/streams';
import { BrowseItem } from '../../../types/api';
import { SHOP_GRID_BREAKPOINTS, SHOP_GRID_SPACING } from './constants';

import s from './ShopGrid.scss';

interface Props {
  items: BrowseItem[];
}

const ShopGrid: React.FC<Props> = ({ items }) => {
  const isMobile = useContext(MobileContext);

  const getDuperbrowseItemId = useCallback(
    (index: number) => {
      const item = items[index];

      if (isDeviation(item)) {
        return getStreamItemId(item);
      }

      const type = getItemType(item);
      return `item-${type}-${getItemId(item)}`;
    },
    [items]
  );

  const renderGridItem = useCallback(
    (element: GridElement) => {
      const { index, width, style } = element;
      const deviation = items[index] as PapiDeviation;
      const key = `shop-grid-${getItemId(deviation)}`;
      const isCommission =
        getDeviationType(deviation) === DeviationTypes.COMMISSION;

      return (
        deviation && (
          <BiLoggerContextProvider value={{ _rankIndex: index }}>
            <ShopCard
              key={key}
              deviation={deviation}
              width={width}
              style={style}
              showDescription={isCommission}
            />
          </BiLoggerContextProvider>
        )
      );
    },
    [items]
  );

  return (
    <div className={s['grid-container']}>
      <StandardGrid
        breakpoints={SHOP_GRID_BREAKPOINTS}
        elementCount={items.length}
        cookieType={MeasuredCookieType.BROWSE}
        getDuperbrowseItemId={getDuperbrowseItemId}
        spacing={SHOP_GRID_SPACING}
        dontScale={!isMobile}
        withShadowSpace={!isMobile}
      >
        {renderGridItem}
      </StandardGrid>
    </div>
  );
};
ShopGrid.displayName = 'ShopGrid';

export default ShopGrid;
